/** @format */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import galaxieClient from "./galaxieClient";

/**
 * Query options factories for races and standings
 * These factories provide queryKey + queryFn together for reuse in both hooks and loaders
 * Single source of truth for endpoints - eliminates duplication in routeUtils
 */
export const raceQueryOptions = {
  list: (seriesID: string, year: string) => ({
    queryKey: ["races", seriesID, year] as const,
    queryFn: async () => {
      const { data } = await galaxieClient.get<Race[]>(
        `/races/?series=${seriesID}&year=${year}`
      );
      return data;
    },
  }),

  detail: (raceId: string) => ({
    queryKey: ["race", raceId] as const,
    queryFn: async () => {
      const { data } = await galaxieClient.get<Race>(`/races/${raceId}/`);
      return data;
    },
  }),

  next: () => ({
    queryKey: ["next_race"] as const,
    queryFn: async () => {
      const { data } = await galaxieClient.get<Race[]>("/next_race/");
      return data;
    },
  }),

  previous: (year?: number) => ({
    queryKey: year ? (["previous_race", year] as const) : (["previous_race"] as const),
    queryFn: async () => {
      const url = year ? `/previous_race/?year=${year}` : "/previous_race/";
      const { data } = await galaxieClient.get<Race[]>(url);
      return data;
    },
  }),

  entries: (raceId: string) => ({
    queryKey: ["race_entries", raceId] as const,
    queryFn: async () => {
      const { data } = await galaxieClient.get<RaceEntries>(
        `/races/${raceId}/entries/`
      );
      return data;
    },
  }),

  placeholderRun: (raceId: string) => ({
    queryKey: ["race_placeholder_run", raceId] as const,
    queryFn: async () => {
      const { data } = await galaxieClient.get<{ placeholderRunId: string }>(
        `/races/${raceId}/placeholder_run/`
      );
      return data;
    },
  }),

  standings: (raceId: string) => ({
    queryKey: ["race_standings", raceId] as const,
    queryFn: async () => {
      const { data } = await galaxieClient.get<Standings[]>(
        `/races/${raceId}/standings/`
      );
      return data;
    },
  }),

  preRaceStats: (raceId: string) => ({
    queryKey: ["pre_race_stats", raceId] as const,
    queryFn: async () => {
      // Backend returns 202 when the cache is cold and a warm task has been
      // enqueued. Throw a typed error so React Query treats it as a failed
      // fetch and retries with backoff (configured on the hook below).
      const response = await galaxieClient.get<
        PreRaceStats | { status: "computing"; raceId: string }
      >(`/races/${raceId}/pre_race_stats/`, {
        timeout: 1000 * 30,
        validateStatus: s => s >= 200 && s < 300,
      });
      if (response.status === 202) {
        throw new Error("pre-race-stats-pending");
      }
      return response.data as PreRaceStats;
    },
  }),
  raceProfile: (raceId: string) => ({
    queryKey: ["race_profile", raceId] as const,
    queryFn: async () => {
      const { data } = await galaxieClient.get<RaceProfile>(
        `/races/${raceId}/race_profile/`,
        {
          timeout: 1000 * 60,
        }
      );
      return data;
    },
  }),
  gRatings: (raceId: string) => ({
    queryKey: ["g_rating", raceId] as const,
    queryFn: async () => {
      const { data } = await galaxieClient.get<GRatingsResponse>(
        `/races/${raceId}/g_rating/`,
        {
          timeout: 1000 * 60, // 1 minute timeout
        }
      );
      return data;
    },
  }),
};

export const standingsQueryOptions = {
  season: (seriesID: string, year: string) => ({
    queryKey: ["season_standings", seriesID, year] as const,
    queryFn: async () => {
      const { data } = await galaxieClient.get<Standings[]>(
        `/standings/?series=${seriesID}&year=${year}`
      );
      return data;
    },
  }),
};

// Hooks now use the query options factories
export const useRacesList = (seriesID: string, year: string, options = {}) =>
  useQuery({
    ...raceQueryOptions.list(seriesID, year),
    ...options,
  });

export const useRace = (raceId: string, options = {}) =>
  useQuery({
    ...raceQueryOptions.detail(raceId),
    ...options,
  });

export const useNextRaces = (options = {}) =>
  useQuery({
    ...raceQueryOptions.next(),
    ...options,
  });

export const usePreviousRaces = (year?: number, options = {}) =>
  useQuery({
    ...raceQueryOptions.previous(year),
    ...options,
  });

export const usePreRaceStats = (raceId: string, options = {}) =>
  useQuery({
    ...raceQueryOptions.preRaceStats(raceId),
    enabled: !!raceId,
    // 202 means "warm task enqueued, retry shortly". Poll with bounded
    // exponential backoff capped at 15s — total wait ~95s, covering the
    // worst-case pre-race-stats compute window.
    retry: (failureCount, error) =>
      error instanceof Error &&
      error.message === "pre-race-stats-pending" &&
      failureCount < 10,
    retryDelay: attempt => Math.min(2000 * 2 ** attempt, 15000),
    ...options,
  });

export const useRaceProfile = (raceId: string, options = {}) =>
  useQuery({
    ...raceQueryOptions.raceProfile(raceId),
    enabled: !!raceId,
    ...options,
  });

export const useGRatings = (
  raceId: string,
  options?: {
    enabled?: boolean;
  }
) => {
  const { enabled = true, ...restOptions } = options ?? {};

  return useQuery({
    ...raceQueryOptions.gRatings(raceId),
    enabled: !!raceId && enabled,
    ...restOptions,
  });
};

export const useRaceEntries = (raceId: string, options = {}) =>
  useQuery({
    ...raceQueryOptions.entries(raceId),
    enabled: !!raceId,
    ...options,
  });

export const useRacePlaceholderRun = (raceId: string, options = {}) =>
  useQuery({
    ...raceQueryOptions.placeholderRun(raceId),
    enabled: !!raceId,
    ...options,
  });

export const useRaceStandings = (raceId: string, options = {}) =>
  useQuery({
    ...raceQueryOptions.standings(raceId),
    enabled: !!raceId,
    ...options,
  });

export const useSeasonStandings = (seriesID: string, year: string, options = {}) =>
  useQuery({
    ...standingsQueryOptions.season(seriesID, year),
    ...options,
  });

/**
 * Fetch championship format metadata for a race.
 * Format is static - cached aggressively since it doesn't change.
 */
export const useRaceFormat = (raceId: string, options = {}) =>
  useQuery({
    queryKey: ["raceFormat", raceId] as const,
    queryFn: async () => {
      const { data } = await galaxieClient.get<ChampionshipFormatMeta | null>(
        `/races/${raceId}/format/`
      );
      return data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes — static within a session, refreshes across sessions
    gcTime: 60 * 60 * 1000, // 1 hour
    enabled: !!raceId,
    ...options,
  });

/**
 * Fetch championship format metadata for a season.
 * Format is static - cached aggressively since it doesn't change.
 */
export const useSeasonFormat = (seriesId: string, year: string, options = {}) =>
  useQuery({
    queryKey: ["seasonFormat", seriesId, year] as const,
    queryFn: async () => {
      const { data } = await galaxieClient.get<ChampionshipFormatMeta | null>(
        `/seasons/${seriesId}/${year}/format/`
      );
      return data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes — static within a session, refreshes across sessions
    gcTime: 60 * 60 * 1000, // 1 hour
    enabled: !!seriesId && !!year,
    ...options,
  });

export const useScrapeRaceRoster = () =>
  useMutation<{ message: string; task_id: string; race_id: string }, Error, string>({
    mutationFn: async (raceId: string) => {
      const { data } = await galaxieClient.post<{
        message: string;
        task_id: string;
        race_id: string;
      }>(`/races/${raceId}/scrape_roster/`);
      return data;
    },
  });

export const useUpdateRace = (raceId: string) => {
  const queryClient = useQueryClient();
  return useMutation<Race, Error, Partial<Race>>({
    mutationFn: async race => {
      const { data } = await galaxieClient.patch<Race>(`/races/${raceId}/`, race);
      return data;
    },
    onMutate: race => {
      const oldRace = queryClient.getQueryData<Race>(["race", raceId]);
      queryClient.setQueryData(["race", raceId], {
        ...oldRace,
        ...race,
      });
      return oldRace;
    },
    onSuccess: (data, _race) => {
      queryClient.setQueryData(["race", raceId], data);
    },
    onError: (_err, _variables, race) => {
      queryClient.setQueryData(["race", raceId], race);
    },
  });
};
